import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AppShell from '../components/AppShell';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import { Badge } from '../components/ui/badge';
import {
  ArrowLeft,
  Save,
  RotateCcw,
  Loader2,
  GitPullRequest,
  MessageSquare,
  Link2,
  FileText,
  AlertTriangle,
  Info
} from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const ANALYZER_CONFIG = {
  stalled_pr: {
    name: 'Stalled Pull Requests',
    description: 'Detect PRs that have been open without updates',
    icon: GitPullRequest,
    fields: [
      {
        key: 'stall_threshold_days',
        label: 'Days before PR is considered stalled',
        description: 'A PR without updates for this many days triggers an alert',
        type: 'number',
        min: 1,
        max: 90,
        default: 7
      },
      {
        key: 'high_threshold_days',
        label: 'Days for high severity',
        description: 'Stalled PRs older than this are marked as high priority',
        type: 'number',
        min: 1,
        max: 90,
        default: 14
      },
      {
        key: 'critical_threshold_days',
        label: 'Days for critical severity',
        description: 'Stalled PRs older than this are marked as critical',
        type: 'number',
        min: 1,
        max: 180,
        default: 30
      }
    ]
  },
  high_discussion: {
    name: 'High Discussion Activity',
    description: 'Detect unusual activity spikes on issues and PRs',
    icon: MessageSquare,
    fields: [
      {
        key: 'comment_threshold',
        label: 'Comment threshold',
        description: 'Number of comments in the time window to trigger an alert',
        type: 'number',
        min: 1,
        max: 100,
        default: 10
      },
      {
        key: 'mention_threshold',
        label: 'Mention threshold',
        description: 'Number of Slack mentions to trigger an alert',
        type: 'number',
        min: 1,
        max: 50,
        default: 5
      },
      {
        key: 'time_window_hours',
        label: 'Time window (hours)',
        description: 'Activity is measured over this time period',
        type: 'number',
        min: 1,
        max: 168,
        default: 24
      }
    ]
  },
  blocked_issue: {
    name: 'Blocked Issues',
    description: 'Detect Linear issues blocked by unresolved dependencies',
    icon: Link2,
    fields: [
      {
        key: 'high_blocker_count',
        label: 'Blockers for high severity',
        description: 'Issues with this many blockers are marked as high priority',
        type: 'number',
        min: 1,
        max: 10,
        default: 2
      },
      {
        key: 'critical_blocker_count',
        label: 'Blockers for critical severity',
        description: 'Issues with this many blockers are marked as critical',
        type: 'number',
        min: 1,
        max: 20,
        default: 3
      }
    ]
  },
  dependency_change: {
    name: 'Dependency Changes',
    description: 'Detect documentation changes that may impact code',
    icon: FileText,
    fields: [
      {
        key: 'importance_threshold',
        label: 'Importance threshold',
        description: 'Minimum importance score (0-1) to trigger analysis',
        type: 'slider',
        min: 0,
        max: 1,
        step: 0.1,
        default: 0.5
      }
    ]
  }
};

export default function AlertSettingsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (user?.role !== 'admin') {
      toast.error('Admin access required');
      navigate('/settings');
      return;
    }
    loadSettings();
  }, [user, navigate]);

  const loadSettings = async () => {
    try {
      const response = await api.get('/admin/alert-settings');
      setSettings(response.data);
      setHasChanges(false);
    } catch (error) {
      toast.error('Failed to load alert settings');
      // Set defaults if API fails
      setSettings({
        stalled_pr: { stall_threshold_days: 7, critical_threshold_days: 30, high_threshold_days: 14 },
        high_discussion: { comment_threshold: 10, mention_threshold: 5, time_window_hours: 24 },
        dependency_change: { importance_threshold: 0.5 },
        blocked_issue: { critical_blocker_count: 3, high_blocker_count: 2 },
        is_default: true
      });
    } finally {
      setLoading(false);
    }
  };

  const updateSetting = (analyzerKey, fieldKey, value) => {
    setSettings(prev => ({
      ...prev,
      [analyzerKey]: {
        ...prev[analyzerKey],
        [fieldKey]: value
      }
    }));
    setHasChanges(true);
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await api.put('/admin/alert-settings', {
        stalled_pr: settings.stalled_pr,
        high_discussion: settings.high_discussion,
        dependency_change: settings.dependency_change,
        blocked_issue: settings.blocked_issue
      });
      toast.success('Alert settings saved');
      setHasChanges(false);
      // Reload to get updated timestamps
      await loadSettings();
    } catch (error) {
      toast.error(`Failed to save settings: ${error.response?.data?.detail || error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = async () => {
    setResetting(true);
    try {
      await api.delete('/admin/alert-settings');
      toast.success('Alert settings reset to defaults');
      await loadSettings();
    } catch (error) {
      toast.error(`Failed to reset settings: ${error.response?.data?.detail || error.message}`);
    } finally {
      setResetting(false);
    }
  };

  const renderField = (analyzerKey, field) => {
    const value = settings[analyzerKey]?.[field.key] ?? field.default;

    if (field.type === 'slider') {
      return (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label>{field.label}</Label>
            <span className="text-sm font-medium">{value.toFixed(1)}</span>
          </div>
          <Slider
            value={[value]}
            onValueChange={([v]) => updateSetting(analyzerKey, field.key, v)}
            min={field.min}
            max={field.max}
            step={field.step}
            className="w-full"
          />
          <p className="text-xs text-muted-foreground">{field.description}</p>
        </div>
      );
    }

    return (
      <div className="space-y-2">
        <Label htmlFor={`${analyzerKey}-${field.key}`}>{field.label}</Label>
        <Input
          id={`${analyzerKey}-${field.key}`}
          type="number"
          min={field.min}
          max={field.max}
          value={value}
          onChange={(e) => updateSetting(analyzerKey, field.key, parseInt(e.target.value) || field.default)}
          className="w-full"
        />
        <p className="text-xs text-muted-foreground">{field.description}</p>
      </div>
    );
  };

  if (loading) {
    return (
      <AppShell>
        <div className="max-w-4xl mx-auto p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-muted rounded w-64"></div>
            <div className="h-64 bg-muted rounded"></div>
          </div>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="max-w-4xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/settings')}
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold">Alert Thresholds</h1>
              <p className="text-muted-foreground mt-1">
                Configure sensitivity thresholds for risk detection
              </p>
            </div>
          </div>
          {settings?.is_default && (
            <Badge variant="outline" className="text-muted-foreground">
              Using Defaults
            </Badge>
          )}
        </div>

        {/* Info Banner */}
        <Card className="p-4 bg-muted/50 border-blue-200">
          <div className="flex gap-3">
            <Info className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium">How Alert Thresholds Work</p>
              <p className="text-muted-foreground mt-1">
                These settings control when risk alerts are triggered. Lower thresholds mean more alerts,
                higher thresholds mean fewer but more significant alerts. Changes apply to all future scans.
              </p>
            </div>
          </div>
        </Card>

        {/* Analyzer Cards */}
        {Object.entries(ANALYZER_CONFIG).map(([analyzerKey, config]) => {
          const Icon = config.icon;
          return (
            <Card key={analyzerKey} className="p-6">
              <div className="flex items-start gap-4 mb-6">
                <div className="p-2 rounded-lg bg-primary/10">
                  <Icon className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold">{config.name}</h2>
                  <p className="text-sm text-muted-foreground">{config.description}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {config.fields.map(field => (
                  <div key={field.key}>
                    {renderField(analyzerKey, field)}
                  </div>
                ))}
              </div>
            </Card>
          );
        })}

        {/* Actions */}
        <div className="flex items-center justify-between pt-4 border-t">
          <Button
            variant="outline"
            onClick={resetToDefaults}
            disabled={resetting || settings?.is_default}
          >
            {resetting ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <RotateCcw className="h-4 w-4 mr-2" />
            )}
            Reset to Defaults
          </Button>

          <Button
            onClick={saveSettings}
            disabled={saving || !hasChanges}
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Save Changes
          </Button>
        </div>

        {hasChanges && (
          <div className="flex items-center gap-2 text-sm text-amber-600">
            <AlertTriangle className="h-4 w-4" />
            You have unsaved changes
          </div>
        )}
      </div>
    </AppShell>
  );
}
